import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { QueryStatus } from '@tanstack/react-query';
import { LENS_LAYOUT, lensTokens } from '~/configs/unifiedAnalyticsTheme';

import AddIcon from '@mui/icons-material/Add';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import SearchIcon from '@mui/icons-material/Search';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import { Box, ButtonBase, IconButton, InputBase, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { AskRichardWordmark, BRAND_COLORS, LensMark } from '~/components/brand';

import RichardMonogram from '~/pages/AskRichardUnified/chat/RichardMonogram';
import {
  deriveSessionRowTitle,
  filterSessions,
  formatCompactRelativeTime,
} from '~/pages/AskRichardUnified/chat/SessionHistoryPanel';
import { RICHARD_MICROCOPY } from '~/pages/AskRichardUnified/chat/microcopy';

import { usePinReportingLeDataSession } from '~/hooks/apis/reporting/usePinReportingLeDataSession';
import { useRecentReportingLeDataSessions } from '~/hooks/apis/reporting/useRecentReportingLeDataSessions';
import useCurrentUser from '~/hooks/useCurrentUser';

import { formatDateWithClock } from '~/services/TimeService';

import type { Lens, LensSpace } from '~/types/leLens';

import { RailBetaBadge } from '../LeLens/BetaBadge';
import { analyticsAreas } from '../LeLens/catalogue/analyticsDomains';
import {
  ACCOMMODATION_NAVIGATION_ITEMS,
  type AccommodationModelId,
  COMMERCIAL_NAVIGATION_GROUPS,
  type CommercialAnalysisId,
} from '../LeLens/catalogue/commercial/navigation';
import { GROWTH_NAVIGATION } from '../LeLens/catalogue/growth/navigation';
import type { GrowthPageId } from '../LeLens/catalogue/growth/types';
import { type LensLibraryDestination, decodeLensLibraryDestination, lensesForDestination } from '../LeLens/lensLibrary';
import { rankTeams } from '../LeLens/library/teamRanking';

import { MAX_PINNED_LENSES } from './lensPins';
import { lensReadFailureText } from './lensReadFailure';
import { changedLensIds } from './lensVisits';
import { compactRecentSessions, keepActiveAtLimit } from './railContent';
import {
  THREAD_PINS_CHANGED_EVENT,
  THREAD_PIN_PREFERENCES_STORAGE_KEY,
  readThreadPinPreferences,
  saveThreadPinPreferences,
} from './threadPins';
import { useLensPinToggle } from './useLensPinToggle';
import { useLensVisits } from './useLensVisits';
import type { ViewKey } from './viewState';

interface RailProps {
  /** Null on contextual pages outside the analytics workspace. */
  view: ViewKey | null;
  activeCatalogueDomainId?: string | null;
  activeCommercialAnalysisId?: CommercialAnalysisId;
  activeGrowthPageId?: GrowthPageId;
  activeAccommodationModelId?: AccommodationModelId | null;
  /** Collapsed, the rail is a 60 px icon strip so the board keeps the room when a pane is open. */
  collapsed?: boolean;
  /** Flips collapsed. Absent, the rail shows no toggle. */
  onToggleCollapsed?: () => void;
  // The real askRichard session id open in the embedded chat, or null before any thread has been
  // opened. Drives both the highlighted rail row and the chat via the seam in LeDataChat's
  // controlledSessionId prop (see index.tsx).
  activeThreadId: string | null;
  activeLensId: string | null;
  onNavigate: (v: ViewKey, id?: string) => void;
  onOpenCatalogueDomain?: (domainId: string) => void;
  onOpenLpcOfferHealth?: () => void;
  onOpenCommercialAnalysis?: (analysisId: CommercialAnalysisId) => void;
  onOpenGrowthPage?: (pageId: GrowthPageId) => void;
  onOpenAccommodationModel?: (model: AccommodationModelId) => void;
  /** Opens the governed-data library without making it the Analytics landing page. */
  onOpenDataCatalogue?: () => void;
  /** Keeps Home and Data catalogue from both appearing selected when the library is open. */
  dataCatalogueActive?: boolean;
  onNewThread: () => void;
  /** Opens the New Lens sheet directly (the composer-first create conversation), with no brief yet. */
  onNewLens: () => void;
  // Real Lenses fetched from svc-reporting. Empty until the list call resolves, whether that ends
  // in success (an empty workspace) or failure (nothing reachable).
  realLenses: Array<Lens>;
  // The raw query status, not collapsed to a boolean: "still loading" and "unreachable" need
  // different rail treatments, a skeleton versus a stated failure with a retry, and collapsing
  // them made an ordinary first load claim the service was unreachable.
  realLensesStatus: QueryStatus;
  onRetryLenses?: () => void;
  /**
   * The failure itself, not just that there was one. A signed-out session and
   * an unreachable service both land here, and they need different things from
   * the reader.
   */
  realLensesError?: unknown;
  /** Folders where the reader holds a role. */
  folders: Array<LensSpace>;
  /** Shows the Certification row in the Data section. Reviewers only. */
  canReviewCertification?: boolean;
  certificationRequestCount?: number;
  onOpenLibrary: (destination: LensLibraryDestination) => void;
}

interface RailRowProps {
  icon: React.ReactNode;
  iconClassName?: string;
  label: string;
  labelTooltip?: string;
  trailing?: React.ReactNode;
  active: boolean;
  onClick: () => void;
  inset?: boolean;
  quiet?: boolean;
  dense?: boolean;
  indent?: number;
}

function RailRow({
  icon,
  iconClassName,
  label,
  labelTooltip,
  trailing,
  active,
  onClick,
  inset = false,
  quiet = false,
  dense = false,
  indent,
}: RailRowProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);

  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      aria-current={active ? 'page' : undefined}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: dense ? 0.75 : 1,
        pl: indent ?? (inset ? 2.25 : 1.25),
        pr: 1,
        minHeight: dense ? 32 : inset ? 32 : 36,
        py: dense ? 0.25 : inset ? 0.5 : 0.625,
        borderRadius: 1,
        textAlign: 'left',
        fontSize: dense ? '13px' : quiet ? '12.5px' : '13.5px',
        lineHeight: 1.25,
        fontWeight: active ? 600 : 400,
        color: active ? tokens.railText : quiet ? tokens.railTextMuted : tokens.railText,
        backgroundColor: active ? (inset ? tokens.railHover : tokens.railActive) : 'transparent',
        boxShadow: 'none',
        transition: 'background-color 120ms ease, color 120ms ease',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        '&:hover': { backgroundColor: active && !inset ? tokens.railActive : tokens.railHover, color: tokens.railText },
        '&:focus-visible': { outline: `2px solid ${tokens.coralAccent}`, outlineOffset: '-2px' },
      }}
    >
      <Box
        className={iconClassName}
        sx={{
          display: 'flex',
          width: 18,
          justifyContent: 'center',
          opacity: 0.85,
          flex: 'none',
          transition: 'opacity 120ms ease',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        {icon}
      </Box>
      <Tooltip disableInteractive placement="right" title={labelTooltip ?? ''}>
        <Box
          component="span"
          sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {label}
        </Box>
      </Tooltip>
      {trailing}
    </ButtonBase>
  );
}

/**
 * The Accommodation page and its disclosure are separate controls: nesting an expand button in
 * a navigation button would be invalid HTML and made keyboard interaction ambiguous.
 */
function AccommodationParentRow({
  active,
  expanded,
  onOpen,
  onToggle,
}: {
  active: boolean;
  expanded: boolean;
  onOpen: () => void;
  onToggle: () => void;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  return (
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: active ? tokens.railActive : 'transparent',
        borderRadius: 1,
        color: active ? tokens.railText : tokens.railTextMuted,
        display: 'flex',
        gap: 0.75,
        minHeight: 30,
        pl: 4.25,
        '&:hover': { backgroundColor: active ? tokens.railActive : tokens.railHover, color: tokens.railText },
      }}
    >
      <IconButton
        aria-controls="commercial-accommodation-model-navigation"
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} Accommodation models`}
        onClick={onToggle}
        size="small"
        sx={{
          borderRadius: 1,
          color: 'inherit',
          flex: '0 0 18px',
          height: 30,
          p: 0,
          width: 18,
          '&:focus-visible': { outline: `2px solid ${tokens.coralAccent}`, outlineOffset: '-2px' },
        }}
      >
        <KeyboardArrowDownIcon sx={{ fontSize: 15, transform: expanded ? 'none' : 'rotate(-90deg)' }} />
      </IconButton>
      <ButtonBase
        aria-current={active ? 'page' : undefined}
        aria-label="Open Accommodation Performance"
        focusRipple
        onClick={onOpen}
        sx={{
          alignItems: 'center',
          borderRadius: 1,
          color: 'inherit',
          display: 'flex',
          flex: '1 1 auto',
          fontSize: '12.75px',
          fontWeight: active ? 600 : 400,
          justifyContent: 'flex-start',
          minHeight: 30,
          minWidth: 0,
          pl: 0,
          pr: 1,
          textAlign: 'left',
          '&:focus-visible': { outline: `2px solid ${tokens.coralAccent}`, outlineOffset: '-2px' },
        }}
      >
        <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Accommodation
        </Box>
      </ButtonBase>
    </Box>
  );
}

function AccommodationNavigation({
  activeModel,
  expanded,
  onOpenModel,
}: {
  activeModel: AccommodationModelId | null | undefined;
  expanded: boolean;
  onOpenModel: (model: AccommodationModelId) => void;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  if (!expanded) {
    return null;
  }

  return (
    <Box
      id="commercial-accommodation-model-navigation"
      sx={{
        display: 'grid',
        gap: '3px',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        // Keep the nested left edge while matching the parent row's full right edge.
        pl: 5,
        pr: 0,
        mt: 0.375,
      }}
    >
      {ACCOMMODATION_NAVIGATION_ITEMS.map((item) => {
        const active = activeModel === item.id;
        return (
          <ButtonBase
            aria-current={active ? 'page' : undefined}
            focusRipple
            key={item.id}
            onClick={() => onOpenModel(item.id)}
            sx={{
              backgroundColor: active ? tokens.railActive : alpha(tokens.railText, 0.045),
              border: '1px solid transparent',
              borderBottomColor: active ? alpha(tokens.coralAccent, 0.82) : 'transparent',
              borderRadius: 1,
              color: active ? tokens.railText : tokens.railTextMuted,
              fontSize: '11.5px',
              fontWeight: active ? 650 : 500,
              justifyContent: 'center',
              minHeight: 30,
              minWidth: 0,
              overflow: 'hidden',
              px: 0.5,
              textAlign: 'center',
              transition:
                'background-color 120ms ease, border-color 120ms ease, color 120ms ease, transform 120ms ease',
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              '&:hover': {
                backgroundColor: active ? tokens.railActive : tokens.railHover,
                color: tokens.railText,
                transform: 'translateY(-1px)',
              },
              '&:focus-visible': { outline: `2px solid ${tokens.coralAccent}`, outlineOffset: '-2px' },
            }}
          >
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.label}
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
}

/**
 * The rail's only section label: 11px, 600, caps, 0.1em tracking, white at 56%, 18px above and
 * 6px below. Actions sit right-aligned in the same row.
 */
function RailSectionLabel({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) {
  const theme = useTheme();
  const tokens = lensTokens(theme);

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        minHeight: 18,
        mb: '6px',
        mt: '18px',
        px: 1.25,
      }}
    >
      <Typography
        component="h3"
        sx={{
          color: alpha(tokens.railText, 0.56),
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.1em',
          lineHeight: 1.2,
          textTransform: 'uppercase',
        }}
      >
        {children}
      </Typography>
      {action ? <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.25, mr: -0.5 }}>{action}</Box> : null}
    </Box>
  );
}

function RailCount({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  return (
    <Box
      aria-hidden
      component="span"
      sx={{
        color: alpha(tokens.railText, 0.56),
        flex: 'none',
        fontSize: '11px',
        fontVariantNumeric: 'tabular-nums',
        mr: 0.5,
      }}
    >
      {children}
    </Box>
  );
}

// A skeleton shaped like the eventual rows, never a bare spinner or placeholder text: the rail's
// own version of the design spec's loading-state rule.
function RailRowSkeleton({ count, indent }: { count: number; indent?: number }) {
  const theme = useTheme();
  const tokens = lensTokens(theme);

  return (
    <Stack spacing="4px" sx={{ pl: indent ?? 1.25, pr: 1.25 }}>
      {Array.from({ length: count }, (_, index) => (
        <Skeleton key={index} variant="rounded" height={26} sx={{ borderRadius: 0.75, bgcolor: tokens.railHover }} />
      ))}
    </Stack>
  );
}

// A considered empty or error row for a nav section: an icon plus a human sentence, never bare
// text sitting alone in the gap where rows would be.
function RailInlineState({
  icon,
  text,
  onRetry,
  indent,
}: {
  icon: React.ReactNode;
  text: string;
  onRetry?: () => void;
  indent?: number;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);

  return (
    <Box sx={{ display: 'flex', gap: 0.875, alignItems: 'flex-start', pl: indent ?? 1.25, pr: 1.25, py: 0.5 }}>
      <Box sx={{ display: 'flex', flex: 'none', mt: '1px', color: tokens.railTextMuted }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '11.5px', lineHeight: 1.5, color: tokens.railTextMuted }}>{text}</Typography>
        {onRetry ? (
          <ButtonBase
            onClick={onRetry}
            sx={{
              color: tokens.railText,
              fontSize: '11px',
              fontWeight: 600,
              mt: 0.25,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Retry
          </ButtonBase>
        ) : null}
      </Box>
    </Box>
  );
}

/** Matches shown for a query, enough to find one without becoming the whole list. */
const RAIL_SEARCH_RESULTS = 12;
/** Recents are shortcuts into work, not a second archive inside the rail. */
const RAIL_THREAD_PAGE_SIZE = 3;
const RAIL_LENSES = MAX_PINNED_LENSES;
/** The viewer's own teams shown under Lenses before the quiet All teams row. */
const RAIL_TEAMS = 3;
function RailSearchField({
  label,
  onChange,
  onClose,
  query,
}: {
  label: string;
  onChange: (query: string) => void;
  onClose: () => void;
  query: string;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);

  return (
    <Box sx={{ mb: 0.5, px: 1.25 }}>
      <InputBase
        autoFocus
        endAdornment={
          <IconButton
            aria-label={`Close ${label.toLocaleLowerCase()}`}
            onClick={onClose}
            size="small"
            sx={{ color: 'inherit', p: 0.25, '&:hover': { backgroundColor: tokens.railHover } }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        }
        inputProps={{ 'aria-label': label }}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            onClose();
          }
        }}
        placeholder={label}
        startAdornment={<SearchIcon sx={{ color: 'inherit', fontSize: 15, mr: 0.75, opacity: 0.7 }} />}
        sx={{
          backgroundColor: 'transparent',
          border: `1px solid ${tokens.railBorder}`,
          borderRadius: 1.25,
          color: 'inherit',
          fontSize: '11.5px',
          minHeight: 32,
          px: 1,
          py: 0,
          width: '100%',
          transition: 'background-color 120ms ease, border-color 120ms ease',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          '& .MuiInputBase-input': {
            padding: 0,
          },
          '& input::placeholder': { color: tokens.railTextMuted, opacity: 1 },
          '&:hover': { backgroundColor: tokens.railHover },
          '&.Mui-focused': { backgroundColor: tokens.railHover, borderColor: tokens.railTextMuted },
        }}
        value={query}
      />
    </Box>
  );
}

function RailLabelButton({
  children,
  label,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);

  return (
    <IconButton
      aria-label={label}
      onClick={onClick}
      size="small"
      sx={{
        color: tokens.railTextMuted,
        height: 24,
        width: 24,
        '&:hover': { backgroundColor: tokens.railHover, color: tokens.railText },
        '&:focus-visible': { outline: `2px solid ${tokens.coralAccent}`, outlineOffset: '-2px' },
      }}
    >
      {children}
    </IconButton>
  );
}

export function Rail({
  view,
  activeCatalogueDomainId,
  activeCommercialAnalysisId = 'overview',
  activeGrowthPageId = 'overview',
  activeAccommodationModelId,
  collapsed = false,
  onToggleCollapsed,
  activeThreadId,
  activeLensId,
  onNavigate,
  onOpenCatalogueDomain,
  onOpenCommercialAnalysis,
  onOpenLpcOfferHealth,
  onOpenGrowthPage,
  onOpenAccommodationModel,
  onOpenDataCatalogue,
  dataCatalogueActive = false,
  onNewThread,
  onNewLens,
  realLenses,
  realLensesError,
  realLensesStatus,
  onRetryLenses,
  folders,
  canReviewCertification = false,
  certificationRequestCount = 0,
  onOpenLibrary,
}: RailProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const { user } = useCurrentUser();
  const [railHovered, setRailHovered] = useState(false);
  const {
    data: threadSessions = [],
    isLoading: threadsLoading,
    isError: threadsError,
    error: threadsErrorValue,
    refetch: refetchThreads,
  } = useRecentReportingLeDataSessions();
  const [threadQuery, setThreadQuery] = useState('');
  const [lensQuery, setLensQuery] = useState('');
  const [threadSearchOpen, setThreadSearchOpen] = useState(false);
  const [lensSearchOpen, setLensSearchOpen] = useState(false);
  const [accommodationModelsOpen, setAccommodationModelsOpen] = useState(
    activeCommercialAnalysisId === 'product-accommodation',
  );
  const [threadLimit, setThreadLimit] = useState(RAIL_THREAD_PAGE_SIZE);
  const trimmedThreadQuery = threadQuery.trim();
  const trimmedLensQuery = lensQuery.trim();
  const searchingThreads = trimmedThreadQuery.length > 0;
  const searchingLenses = trimmedLensQuery.length > 0;
  // Keep the compact age true while someone leaves the workspace open.
  const [recencyNow, setRecencyNow] = useState(() => Math.floor(Date.now() / 60_000) * 60_000);
  useEffect(() => {
    const timer = window.setInterval(() => setRecencyNow(Math.floor(Date.now() / 60_000) * 60_000), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    if (activeCommercialAnalysisId === 'product-accommodation') {
      setAccommodationModelsOpen(true);
    }
  }, [activeCommercialAnalysisId]);
  // A Lens pin is a personal working-set preference, rather than shared dashboard state. Keep it
  // in the browser so pinning never changes someone else's rail or needs a write to the reporting
  // service just to remember a shortcut.
  const { pinnedIds: pinnedLensIds, toggle: toggleLensPin } = useLensPinToggle(
    realLensesStatus === 'success' ? realLenses.map((lens) => lens.id) : undefined,
  );
  const [threadPinPreferences, setThreadPinPreferences] = useState(readThreadPinPreferences);
  const threadPinPreferencesRef = useRef(threadPinPreferences);
  threadPinPreferencesRef.current = threadPinPreferences;

  useEffect(() => {
    const syncThreadPinPreferences = () => {
      const next = readThreadPinPreferences();
      threadPinPreferencesRef.current = next;
      setThreadPinPreferences(next);
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === THREAD_PIN_PREFERENCES_STORAGE_KEY || event.key === null) {
        syncThreadPinPreferences();
      }
    };

    window.addEventListener(THREAD_PINS_CHANGED_EVENT, syncThreadPinPreferences);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(THREAD_PINS_CHANGED_EVENT, syncThreadPinPreferences);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  // What this reader has already opened, and at which version. It answers two return-visit
  // questions the Lens list on its own cannot: which boards have moved on since they last looked,
  // and what a pinned board is called when the list itself did not load.
  const lensVisits = useLensVisits();
  const changedLenses = useMemo(() => changedLensIds(realLenses, lensVisits), [realLenses, lensVisits]);
  // The rail shows a handful of work at rest. Each list owns its search so the
  // query and results cannot be mistaken for the neighbouring content type.
  const threadIsPinned = useCallback(
    (session: { sessionId: string; pinnedAt?: string | null }) =>
      threadPinPreferences[session.sessionId] ?? Boolean(session.pinnedAt),
    [threadPinPreferences],
  );
  const { pinnedThreads, unpinnedThreads } = useMemo(() => {
    const pinned: typeof threadSessions = [];
    const unpinned: typeof threadSessions = [];
    threadSessions.forEach((session) => (threadIsPinned(session) ? pinned : unpinned).push(session));
    return { pinnedThreads: pinned, unpinnedThreads: unpinned };
  }, [threadIsPinned, threadSessions]);
  const recentThreads = useMemo(() => {
    if (trimmedThreadQuery) {
      return filterSessions(threadSessions, trimmedThreadQuery).slice(0, RAIL_SEARCH_RESULTS);
    }
    return compactRecentSessions(unpinnedThreads, activeThreadId, threadLimit);
  }, [activeThreadId, threadLimit, trimmedThreadQuery, threadSessions, unpinnedThreads]);
  const hiddenThreads = searchingThreads ? 0 : Math.max(0, unpinnedThreads.length - recentThreads.length);
  // Pinned conversations lead the list without becoming a separate section.
  // The pin icon carries the distinction, so the rail can stay compact and the
  // thread order remains stable: pinned first, then recent.
  const threadGroups = useMemo(() => {
    if (searchingThreads) {
      return recentThreads.length ? [{ label: undefined, sessions: recentThreads }] : [];
    }
    const sessions = [...pinnedThreads, ...recentThreads];
    return sessions.length ? [{ label: undefined, sessions }] : [];
  }, [pinnedThreads, recentThreads, searchingThreads]);
  const pinSession = usePinReportingLeDataSession();
  const updateThreadPinPreferences = (next: Record<string, boolean>) => {
    threadPinPreferencesRef.current = next;
    setThreadPinPreferences(next);
    saveThreadPinPreferences(next);
  };
  const pinnedLensIdSet = useMemo(() => new Set(pinnedLensIds), [pinnedLensIds]);
  const pinnedDashboardCandidates = useMemo(
    () =>
      pinnedLensIds
        .map((id) => realLenses.find((lens) => lens.id === id))
        .filter((lens): lens is Lens => Boolean(lens)),
    [pinnedLensIds, realLenses],
  );
  const pinnedLenses = useMemo(
    () => keepActiveAtLimit(pinnedDashboardCandidates, activeLensId, RAIL_LENSES),
    [activeLensId, pinnedDashboardCandidates],
  );
  const matchingLenses = useMemo(() => {
    const needle = trimmedLensQuery.toLocaleLowerCase();
    return needle ? realLenses.filter((lens) => lens.title.toLocaleLowerCase().includes(needle)) : realLenses;
  }, [realLenses, trimmedLensQuery]);
  const visibleLenses = searchingLenses ? matchingLenses.slice(0, RAIL_SEARCH_RESULTS) : [];
  const libraryDestination =
    view === 'lensIndex' ? decodeLensLibraryDestination(new URLSearchParams(window.location.search)) : null;
  const visibleTeams = keepActiveAtLimit(
    rankTeams(folders, realLenses, lensVisits, user?.email),
    libraryDestination?.startsWith('space:') ? libraryDestination.slice('space:'.length) : null,
    RAIL_TEAMS,
  );
  const lensCounts = useMemo(
    () => ({ mine: lensesForDestination(realLenses, 'mine', user?.email).length }),
    [realLenses, user?.email],
  );
  const showLensCounts = realLensesStatus === 'success';
  // Domains belong to the Business Pulse page. They show only while that page is open, so ten
  // domains cost the rail nothing the rest of the time.
  const showPulseDomains = view === 'catalogue' && !dataCatalogueActive;
  // Only the pinned boards this browser has a name for. A pin with no remembered visit is an id
  // and nothing else, and an id is not something to show a reader.
  const unavailablePinnedLenses = useMemo(
    () =>
      pinnedLensIds
        .map((id) => ({ id, title: lensVisits[id]?.title }))
        .filter((pinned): pinned is { id: string; title: string } => Boolean(pinned.title)),
    [pinnedLensIds, lensVisits],
  );
  // Counted on the matches, not on what fits, so a heading never claims fewer
  // than the search found.
  const threadMatchCount = searchingThreads ? filterSessions(threadSessions, trimmedThreadQuery).length : 0;

  const railWidthTransition = {
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
      easing: theme.transitions.easing.sharp,
    }),
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  };
  const stripButtonSx = {
    borderRadius: 1,
    color: tokens.railText,
    height: 36,
    width: 36,
    '&:hover': { backgroundColor: tokens.railHover },
    '&[aria-current="page"]': { backgroundColor: tokens.railActive },
    '&:focus-visible': { outline: `2px solid ${tokens.coralAccent}`, outlineOffset: '-2px' },
  };
  const expandToggle = onToggleCollapsed ? (
    <Tooltip placement="right" title="Expand navigation">
      <IconButton
        aria-expanded={false}
        aria-label="Expand navigation"
        onClick={onToggleCollapsed}
        size="small"
        sx={stripButtonSx}
      >
        <KeyboardDoubleArrowRightIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : null;
  const strip = (
    <Box
      aria-label="Workspace"
      component="nav"
      sx={{
        alignItems: 'center',
        backgroundColor: tokens.rail,
        boxSizing: 'border-box',
        color: tokens.railText,
        display: 'flex',
        flex: 'none',
        flexDirection: 'column',
        gap: 0.5,
        height: '100%',
        pb: 2,
        pt: 1,
        width: LENS_LAYOUT.railCollapsedWidth,
        ...railWidthTransition,
      }}
    >
      {expandToggle}
      <ButtonBase
        aria-label="askRichard home"
        onClick={() => onNavigate('landing')}
        sx={{
          borderRadius: 1,
          mb: 1,
          mt: 0.5,
          p: 0.5,
          '&:hover': { backgroundColor: tokens.railHover },
        }}
      >
        <RichardMonogram inverted sx={{ fontSize: 30 }} />
      </ButtonBase>
      <Tooltip placement="right" title="Ask a question">
        <IconButton
          aria-label="Ask a question"
          onClick={onNewThread}
          size="small"
          sx={{
            ...stripButtonSx,
            backgroundColor: tokens.coralAccent,
            color: BRAND_COLORS.jetBlack,
            mb: 1,
            '&:hover': { backgroundColor: tokens.coralAccent, filter: 'brightness(0.96)' },
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip placement="right" title="Business Pulse">
        <IconButton
          aria-current={view === 'catalogue' && !dataCatalogueActive ? 'page' : undefined}
          aria-label="Business Pulse"
          onClick={() => onNavigate('catalogue')}
          size="small"
          sx={stripButtonSx}
        >
          <InsightsOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip placement="right" title="Threads">
        <IconButton
          aria-current={view === 'thread' ? 'page' : undefined}
          aria-label="Threads"
          onClick={() => (activeThreadId ? onNavigate('thread', activeThreadId) : onNewThread())}
          size="small"
          sx={stripButtonSx}
        >
          <ForumOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip placement="right" title="Lenses">
        <IconButton
          aria-current={view === 'lens' || view === 'lensIndex' ? 'page' : undefined}
          aria-label="Lenses"
          onClick={() => onOpenLibrary('home')}
          size="small"
          sx={stripButtonSx}
        >
          <LensMark inverted size={20} />
        </IconButton>
      </Tooltip>
      <Tooltip placement="right" title="New Lens">
        <IconButton aria-label="New Lens" onClick={onNewLens} size="small" sx={stripButtonSx}>
          <Box
            sx={{
              alignItems: 'center',
              border: `1.5px solid ${tokens.railText}`,
              borderRadius: '50%',
              display: 'flex',
              height: 18,
              justifyContent: 'center',
              width: 18,
            }}
          >
            <AddIcon sx={{ fontSize: 13 }} />
          </Box>
        </IconButton>
      </Tooltip>
      {onOpenDataCatalogue && (
        <Tooltip placement="right" title="Data catalogue">
          <IconButton
            aria-current={dataCatalogueActive ? 'page' : undefined}
            aria-label="Data catalogue"
            onClick={onOpenDataCatalogue}
            size="small"
            sx={stripButtonSx}
          >
            <DatasetOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Box sx={{ flex: 1 }} />
    </Box>
  );

  const fadeSx = (shown: boolean) => ({
    opacity: shown ? 1 : 0,
    pointerEvents: shown ? ('auto' as const) : ('none' as const),
    transition: 'opacity 120ms ease',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
    '&:focus-visible': {
      opacity: 1,
      outline: `2px solid ${tokens.coralAccent}`,
      outlineOffset: '2px',
      pointerEvents: 'auto',
    },
  });
  // The collapse control takes no space of its own. It fades in beside the wordmark while the
  // pointer is anywhere in the rail, and stays visible while it has keyboard focus.
  const collapseButton = onToggleCollapsed ? (
    <Tooltip placement="right" title="Collapse navigation">
      <IconButton
        aria-expanded
        aria-label="Collapse navigation"
        onClick={onToggleCollapsed}
        size="small"
        sx={{
          color: tokens.railTextMuted,
          position: 'absolute',
          right: 6,
          '&:hover': { backgroundColor: tokens.railHover, color: tokens.railText },
          ...fadeSx(railHovered),
        }}
      >
        <KeyboardDoubleArrowLeftIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : null;

  const expandedRail = (
    <Box
      component="nav"
      aria-label="Workspace"
      onMouseEnter={onToggleCollapsed ? () => setRailHovered(true) : undefined}
      onMouseLeave={onToggleCollapsed ? () => setRailHovered(false) : undefined}
      sx={{
        width: LENS_LAYOUT.railWidth,
        height: '100%',
        ...railWidthTransition,
        flex: 'none',
        backgroundColor: tokens.rail,
        color: tokens.railText,
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        // The rail remains independently scrollable (wheel, trackpad and keyboard), but it
        // should not read as a permanent second browser scrollbar. Reveal a narrow cue only
        // while the reader is engaging with this navigation.
        scrollbarWidth: 'thin',
        scrollbarColor: 'transparent transparent',
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': { backgroundColor: 'transparent', borderRadius: 999 },
        '&:hover, &:focus-within': {
          scrollbarColor: `${tokens.railTextMuted} transparent`,
          '&::-webkit-scrollbar-thumb': { backgroundColor: tokens.railTextMuted },
        },
        px: 2,
        pt: 0,
        pb: 3,
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: tokens.rail,
          borderBottom: `1px solid ${tokens.railBorder}`,
          display: 'flex',
          flex: 'none',
          minHeight: 68,
          position: 'sticky',
          top: 0,
          width: '100%',
          zIndex: 1,
        }}
      >
        <ButtonBase
          aria-label="askRichard home"
          onClick={() => onNavigate('landing')}
          sx={{
            alignItems: 'center',
            borderRadius: 0,
            display: 'flex',
            flex: 1,
            gap: 1,
            justifyContent: 'center',
            minHeight: 68,
            '&:focus-visible': { outline: `2px solid ${tokens.coralAccent}`, outlineOffset: '-2px' },
          }}
        >
          <RichardMonogram inverted sx={{ flexShrink: 0, fontSize: 30 }} />
          <AskRichardWordmark inverted size={22} />
        </ButtonBase>
        {collapseButton}
      </Box>
      <ButtonBase
        aria-label="Ask a question"
        onClick={onNewThread}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          backgroundColor: tokens.coralAccent,
          color: BRAND_COLORS.jetBlack,
          fontWeight: 600,
          fontSize: '13px',
          borderRadius: 1.25,
          minHeight: 34,
          py: 0.625,
          px: 1.75,
          mx: 0.75,
          mt: 1.75,
          mb: 1.75,
          boxShadow: 'none',
          '&:hover': { filter: 'brightness(0.96)' },
          '&:focus-visible': { outline: `2px solid ${tokens.coralAccent}`, outlineOffset: '2px' },
        }}
      >
        <AddIcon sx={{ fontSize: 15 }} />
        Ask a question
      </ButtonBase>

      <RailSectionLabel
        action={
          !threadSearchOpen ? (
            <RailLabelButton label="Search threads" onClick={() => setThreadSearchOpen(true)}>
              <SearchIcon sx={{ fontSize: 15 }} />
            </RailLabelButton>
          ) : undefined
        }
      >
        {searchingThreads ? `Threads (${threadMatchCount})` : 'Threads'}
      </RailSectionLabel>
      {threadSearchOpen && (
        <RailSearchField
          label="Search threads"
          onChange={setThreadQuery}
          onClose={() => {
            setThreadQuery('');
            setThreadSearchOpen(false);
          }}
          query={threadQuery}
        />
      )}
      {threadsLoading && <RailRowSkeleton count={3} />}
      {!threadsLoading && threadsError && (
        <RailInlineState
          icon={<ErrorOutlineIcon sx={{ fontSize: 15 }} />}
          text={threadsErrorValue instanceof Error ? threadsErrorValue.message : 'Failed to load recent sessions.'}
          onRetry={() => void refetchThreads()}
        />
      )}
      {!threadsLoading && !threadsError && searchingThreads && recentThreads.length === 0 && (
        <RailInlineState icon={<ChatBubbleOutlineIcon sx={{ fontSize: 15 }} />} text="No thread matches that." />
      )}
      {!threadsLoading && !threadsError && !searchingThreads && threadSessions.length === 0 && (
        <RailInlineState icon={<ForumOutlinedIcon sx={{ fontSize: 15 }} />} text={RICHARD_MICROCOPY.emptySidebar} />
      )}
      {!threadsLoading && !threadsError && threadGroups.length > 0 && (
        <Stack spacing={0}>
          {threadGroups.map((group) => (
            <React.Fragment key={group.label ?? 'matches'}>
              {group.sessions.map((session) => {
                const title = deriveSessionRowTitle(session);
                const age = formatCompactRelativeTime(session.updatedAt, recencyNow);
                const isPinned = threadIsPinned(session);

                return (
                  <Box
                    key={session.sessionId}
                    sx={{
                      position: 'relative',
                      '&:hover .lens-rail-pin': { opacity: 1 },
                      '&:focus-within .lens-rail-pin': { opacity: 1 },
                    }}
                  >
                    <RailRow
                      dense
                      icon={<ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />}
                      label={title}
                      trailing={
                        age ? (
                          <Tooltip placement="right" title={`Updated ${formatDateWithClock(session.updatedAt)}`}>
                            <Typography
                              className="rail-thread-age"
                              component="span"
                              sx={{
                                color: tokens.railTextMuted,
                                flex: 'none',
                                fontSize: '11px',
                                lineHeight: 1,
                                mr: 2.5,
                                opacity: 0.9,
                                transition: 'opacity 120ms ease',
                              }}
                            >
                              {age}
                            </Typography>
                          </Tooltip>
                        ) : undefined
                      }
                      active={view === 'thread' && activeThreadId === session.sessionId}
                      onClick={() => onNavigate('thread', session.sessionId)}
                    />
                    {/* A sibling rather than a child: the row is itself a button,
                        and a button inside a button is neither valid nor clickable. */}
                    <IconButton
                      aria-label={isPinned ? `Unpin ${title}` : `Pin ${title}`}
                      className="lens-rail-pin"
                      disabled={pinSession.isPending}
                      onClick={() => {
                        const pinned = !isPinned;
                        const previousPreferences = threadPinPreferencesRef.current;
                        const nextPreferences = { ...previousPreferences, [session.sessionId]: pinned };
                        updateThreadPinPreferences(nextPreferences);
                        pinSession.mutate(
                          { pinned, sessionId: session.sessionId },
                          {
                            onError: () => {
                              // A newer local/tab update wins over the failed request we started.
                              if (threadPinPreferencesRef.current[session.sessionId] === pinned) {
                                updateThreadPinPreferences(previousPreferences);
                              }
                            },
                          },
                        );
                      }}
                      size="small"
                      sx={{
                        color: 'inherit',
                        // Visible at rest only once pinned; otherwise it waits for hover or
                        // keyboard focus on the row (the row's own hover/focus-within rule
                        // above brings it to full opacity).
                        opacity: isPinned ? 0.9 : 0,
                        transition: 'opacity 120ms ease',
                        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        '&:focus-visible': { opacity: 1 },
                      }}
                    >
                      {isPinned ? <PushPinIcon sx={{ fontSize: 14 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 14 }} />}
                    </IconButton>
                  </Box>
                );
              })}
            </React.Fragment>
          ))}
        </Stack>
      )}
      {!searchingThreads && hiddenThreads > 0 && (
        <RailRow
          dense
          quiet
          icon={null}
          label="View more"
          labelTooltip={`View ${Math.min(RAIL_THREAD_PAGE_SIZE, hiddenThreads)} older threads`}
          active={false}
          onClick={() => setThreadLimit((current) => current + RAIL_THREAD_PAGE_SIZE)}
        />
      )}
      <RailSectionLabel
        action={
          !lensSearchOpen ? (
            <>
              <RailLabelButton label="Search lenses" onClick={() => setLensSearchOpen(true)}>
                <SearchIcon sx={{ fontSize: 15 }} />
              </RailLabelButton>
              <RailLabelButton label="New Lens" onClick={onNewLens}>
                <AddIcon sx={{ fontSize: 16 }} />
              </RailLabelButton>
            </>
          ) : undefined
        }
      >
        {searchingLenses ? `Lenses (${matchingLenses.length})` : 'Lenses'}
      </RailSectionLabel>
      {lensSearchOpen && (
        <RailSearchField
          label="Search lenses"
          onChange={setLensQuery}
          onClose={() => {
            setLensQuery('');
            setLensSearchOpen(false);
          }}
          query={lensQuery}
        />
      )}
      {!searchingLenses && (
        <Stack spacing="2px">
          <RailRow
            icon={<LensMark inverted size={18} />}
            label="Home"
            active={libraryDestination === 'home'}
            onClick={() => onOpenLibrary('home')}
          />
          <RailRow
            icon={<PersonOutlineIcon sx={{ fontSize: 16 }} />}
            label="My Lenses"
            trailing={showLensCounts ? <RailCount>{lensCounts.mine}</RailCount> : undefined}
            active={libraryDestination === 'mine'}
            onClick={() => onOpenLibrary('mine')}
          />
          <RailRow
            icon={<GridViewOutlinedIcon sx={{ fontSize: 16 }} />}
            label="Library"
            active={
              libraryDestination === 'teams' ||
              (Boolean(libraryDestination?.startsWith('space:')) &&
                !visibleTeams.some((space) => libraryDestination === `space:${space.id}`))
            }
            onClick={() => onOpenLibrary('teams')}
          />
          {visibleTeams.map((space) => (
            <RailRow
              dense
              icon={<GroupsOutlinedIcon sx={{ fontSize: 14 }} />}
              indent={3.25}
              key={space.id}
              label={space.name}
              labelTooltip={space.name}
              active={libraryDestination === `space:${space.id}`}
              onClick={() => onOpenLibrary(`space:${space.id}`)}
            />
          ))}
        </Stack>
      )}
      <>
        {!searchingLenses &&
          pinnedLenses.map((lens, index) => (
            <Box
              key={lens.id}
              sx={{
                position: 'relative',
                ...(index === 0 ? { borderTop: `1px solid ${tokens.railBorder}`, mt: '6px', pt: '6px' } : {}),
                '&:hover .lens-rail-pinned-unpin, &:focus-within .lens-rail-pinned-unpin': { opacity: 1 },
              }}
            >
              <RailRow
                dense
                indent={3.25}
                icon={<LensMark inverted size={15} />}
                label={lens.title}
                labelTooltip={lens.title}
                active={view === 'lens' && activeLensId === lens.id}
                onClick={() => onNavigate('lens', lens.id)}
              />
              <IconButton
                aria-label={`Unpin ${lens.title}`}
                className="lens-rail-pinned-unpin"
                onClick={() => toggleLensPin(lens.id)}
                size="small"
                sx={{
                  color: 'inherit',
                  opacity: 0,
                  position: 'absolute',
                  right: 4,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  transition: 'opacity 120ms ease',
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  '&:focus-visible': { opacity: 1 },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        {realLensesStatus === 'pending' && <RailRowSkeleton count={3} />}
        {realLensesStatus === 'error' && (
          <>
            <RailInlineState
              icon={<ErrorOutlineIcon sx={{ fontSize: 15 }} />}
              text={lensReadFailureText(realLensesError)}
              onRetry={onRetryLenses}
            />
            {/* A reader who pinned a board and came back to find the section replaced by a failure
              has no way of knowing whether their working set still exists. The names are the ones
              remembered from their own last visit, so the rows state that they cannot be opened
              yet rather than pretending to be live. Retry above is the one recovery action. */}
            {unavailablePinnedLenses.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                {unavailablePinnedLenses.map((pinned) => (
                  <Box
                    key={pinned.id}
                    title="Pinned, not available yet"
                    sx={{
                      alignItems: 'center',
                      color: tokens.railTextMuted,
                      display: 'flex',
                      fontSize: '13.5px',
                      gap: 1.125,
                      opacity: 0.7,
                      px: 1.25,
                      py: 0.875,
                    }}
                  >
                    <Box sx={{ display: 'flex', flex: 'none', justifyContent: 'center', width: 16 }}>
                      <LensMark inverted size={18} />
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {pinned.title}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
        {realLensesStatus === 'success' && searchingLenses && matchingLenses.length === 0 && (
          <RailInlineState icon={<LensMark inverted size={18} />} text="No Lens matches that." />
        )}
        {realLensesStatus === 'success' && visibleLenses.length > 0 && (
          <Stack spacing={0}>
            {visibleLenses.map((lens) => {
              const isPinned = pinnedLensIdSet.has(lens.id);

              return (
                <Box
                  key={lens.id}
                  sx={{
                    position: 'relative',
                    '&:hover .lens-rail-lens-pin, &:focus-within .lens-rail-lens-pin': { opacity: 1 },
                  }}
                >
                  <RailRow
                    dense
                    icon={<LensMark inverted size={15} />}
                    label={lens.title}
                    labelTooltip={lens.title}
                    active={view === 'lens' && activeLensId === lens.id}
                    onClick={() => onNavigate('lens', lens.id)}
                    trailing={
                      <Box component="span" sx={{ alignItems: 'center', display: 'flex', gap: 0.625, mr: 2.25 }}>
                        {changedLenses.has(lens.id) && (
                          <Tooltip placement="right" title="Edited since you last opened this Lens">
                            <Box
                              aria-label="Edited since your last visit"
                              component="span"
                              role="img"
                              sx={{
                                backgroundColor: tokens.coralAccent,
                                borderRadius: '50%',
                                flex: 'none',
                                height: 6,
                                width: 6,
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    }
                  />
                  <IconButton
                    aria-label={isPinned ? `Unpin ${lens.title}` : `Pin ${lens.title}`}
                    className="lens-rail-lens-pin"
                    onClick={() => toggleLensPin(lens.id)}
                    size="small"
                    sx={{
                      color: 'inherit',
                      // Same rule as the thread pin: visible at rest only once pinned,
                      // otherwise revealed by row hover or keyboard focus.
                      opacity: isPinned ? 0.9 : 0,
                      transition: 'opacity 120ms ease',
                      '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                      position: 'absolute',
                      right: 4,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      '&:focus-visible': { opacity: 1 },
                    }}
                  >
                    {isPinned ? <PushPinIcon sx={{ fontSize: 14 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 14 }} />}
                  </IconButton>
                </Box>
              );
            })}
          </Stack>
        )}
      </>
      <RailSectionLabel>Business Pulse</RailSectionLabel>
      <Stack spacing="2px">
        <RailRow
          icon={<InsightsOutlinedIcon sx={{ fontSize: 16 }} />}
          label="Business Pulse"
          trailing={<RailBetaBadge />}
          active={view === 'catalogue' && !dataCatalogueActive}
          onClick={() => onNavigate('catalogue')}
        />
        {showPulseDomains &&
          onOpenCatalogueDomain &&
          analyticsAreas.map((area) => {
            const active = view === 'catalogue' && activeCatalogueDomainId === area.id;

            return (
              <React.Fragment key={area.id}>
                <RailRow
                  dense
                  inset
                  quiet
                  icon={
                    <Box
                      sx={{
                        bgcolor: active ? tokens.coralAccent : tokens.railTextMuted,
                        borderRadius: '50%',
                        height: active ? 6 : 5,
                        opacity: active ? 1 : 0.72,
                        width: active ? 6 : 5,
                      }}
                    />
                  }
                  label={area.name}
                  active={active}
                  onClick={() => onOpenCatalogueDomain(area.id)}
                />
                {area.id === 'commercial' && active && onOpenCommercialAnalysis ? (
                  <Stack spacing="1px" sx={{ pb: 0.25 }}>
                    {COMMERCIAL_NAVIGATION_GROUPS.flatMap((group) => group.items).map((item) => {
                      if (item.id === 'product-accommodation') {
                        const visibleAccommodationModelSelected = ACCOMMODATION_NAVIGATION_ITEMS.some(
                          ({ id }) => id === activeAccommodationModelId,
                        );
                        const parentActive =
                          activeCommercialAnalysisId === 'product-accommodation' && !visibleAccommodationModelSelected;
                        return (
                          <React.Fragment key={item.id}>
                            <AccommodationParentRow
                              active={parentActive}
                              expanded={accommodationModelsOpen}
                              onOpen={() => onOpenCommercialAnalysis(item.id)}
                              onToggle={() => setAccommodationModelsOpen((current) => !current)}
                            />
                            <AccommodationNavigation
                              activeModel={activeAccommodationModelId}
                              expanded={accommodationModelsOpen}
                              onOpenModel={(model) => {
                                if (onOpenAccommodationModel) {
                                  onOpenAccommodationModel(model);
                                } else {
                                  onOpenCommercialAnalysis(item.id);
                                }
                              }}
                            />
                          </React.Fragment>
                        );
                      }
                      return (
                        <RailRow
                          active={activeCommercialAnalysisId === item.id}
                          dense
                          icon={
                            <Box
                              sx={{
                                bgcolor:
                                  activeCommercialAnalysisId === item.id ? tokens.coralAccent : tokens.railTextMuted,
                                borderRadius: '50%',
                                height: 4,
                                width: 4,
                              }}
                            />
                          }
                          indent={4.25}
                          key={item.id}
                          label={item.label}
                          onClick={() => onOpenCommercialAnalysis(item.id)}
                          quiet
                        />
                      );
                    })}
                    {onOpenLpcOfferHealth && (
                      <RailRow
                        dense
                        quiet
                        indent={4.25}
                        active={false}
                        icon={<Box sx={{ bgcolor: tokens.railTextMuted, borderRadius: '50%', height: 4, width: 4 }} />}
                        label="LPC Offer Health"
                        onClick={onOpenLpcOfferHealth}
                      />
                    )}
                  </Stack>
                ) : null}
                {area.id === 'growth' && active && onOpenGrowthPage ? (
                  <Stack spacing="1px" sx={{ pb: 0.25 }}>
                    {GROWTH_NAVIGATION.flatMap((group) => [group, ...(group.children ?? [])]).map((item) => (
                      <RailRow
                        active={activeGrowthPageId === item.id}
                        dense
                        icon={
                          <Box
                            sx={{
                              bgcolor: activeGrowthPageId === item.id ? tokens.coralAccent : tokens.railTextMuted,
                              borderRadius: '50%',
                              height: 4,
                              width: 4,
                            }}
                          />
                        }
                        indent={4.25}
                        key={item.id}
                        label={item.label}
                        onClick={() => onOpenGrowthPage(item.id)}
                        quiet
                      />
                    ))}
                  </Stack>
                ) : null}
              </React.Fragment>
            );
          })}
      </Stack>
      <RailSectionLabel>Data</RailSectionLabel>
      <Stack spacing="2px">
        {onOpenDataCatalogue && (
          <RailRow
            dense
            icon={<DatasetOutlinedIcon sx={{ fontSize: 15 }} />}
            label="Data catalogue"
            trailing={<RailBetaBadge />}
            active={dataCatalogueActive}
            onClick={onOpenDataCatalogue}
          />
        )}
        <RailRow
          dense
          icon={<MenuBookOutlinedIcon sx={{ fontSize: 15 }} />}
          label="Using data"
          active={view === 'dataGuide'}
          onClick={() => onNavigate('dataGuide')}
        />
        {canReviewCertification && (
          <RailRow
            dense
            icon={<VerifiedOutlinedIcon sx={{ fontSize: 15 }} />}
            label="Certification"
            labelTooltip={
              certificationRequestCount > 0 ? `${certificationRequestCount} awaiting certification` : undefined
            }
            trailing={certificationRequestCount > 0 ? <RailCount>{certificationRequestCount}</RailCount> : undefined}
            active={view === 'certification'}
            onClick={() => onNavigate('certification')}
          />
        )}
      </Stack>
    </Box>
  );

  return collapsed ? strip : expandedRail;
}

export default Rail;
